'use client';

import { FlaskConical } from 'lucide-react';
import { useHoverTip } from '@/components/layout/HoverTip';
import { EscudoUda } from './EscudoUda';
import { tarjetaDeLinea, textoOcultoDeLinea, type AreasLinea } from './lineasDisplay';
import styles from './sidebar.module.css';

const SIN_AREAS: AreasLinea = { ocde: [], sies: [] };

interface LineaRowProps {
	name: string;
	/**
	 * Institutional line (the UDA catalogue): the UDA shield on the left. A custom (free-text) line has the flask.
	 * Defaults to institutional.
	 */
	institucional?: boolean;
	/**
	 * Units that used to be shown as a sub-label ("Enfermería · Medicina"): only when the lines span several
	 * units (RLM-03). The row no longer draws them (the unit lives in the tooltip); they stay in the row's
	 * `sr-only` text so the served HTML keeps them.
	 */
	unidades?: readonly string[];
	/**
	 * EVERY unit that backs the line (`LineaVista.unidadesDeLinea`). The tooltip always lists them, even when
	 * the row shows none. Omitted: the tooltip falls back to `unidades`.
	 */
	unidadesDeLinea?: readonly string[];
	/**
	 * OCDE and SIES areas of an institutional line. They go in the hover tooltip (the visual surface) and,
	 * as `sr-only` text inside the row, in the served HTML, so crawlers and screen readers keep them
	 * (SEO rule: the tooltip only exists while hovering). Custom lines have none.
	 */
	areas?: AreasLinea;
	/**
	 * Slug de la línea para navegar a su ficha. DESHABILITADO en v1: las páginas
	 * de líneas todavía no existen. Cuando se construyan, reactivar la navegación
	 * (ver la tarea en Horion, proyecto SIIV). Por ahora la fila es texto, sin link.
	 */
	href?: string;
}

// Fila de una línea (institucional o personalizada): UNA sola línea con el icono a la izquierda, en gris (el escudo UDA
// para las del catálogo, el matraz para las personalizadas), y el nombre recortado con elipsis cuando no cabe. Sin
// insignias ni rótulo de grupo: el tipo de línea lo dicen los títulos de las dos secciones donde vive la fila
// ("Líneas de investigación UDA" / "Otras líneas de investigación", `gruposDeLineas`). No hay botón "…" ni
// sub-etiqueta de unidad: el nombre completo, la unidad y las áreas salen en la etiqueta de la app (`HoverTip`),
// una tarjeta anclada al extremo derecho de la fila: encabezado con el nombre completo y filas con la unidad
// (siempre) y las áreas OCDE y SIES.
//
// Sin mouse también se llega a ella (`touch`): la fila recibe el foco con Tab (muestra la etiqueta, Escape la
// cierra), un toque en la fila la abre y un toque en cualquier otro lado la cierra; y la etiqueta se ve en todos
// los anchos (las demás se apagan bajo 1024px).
//
// No lleva `title`: el del navegador llega tarde y se dibujaría encima de la etiqueta. El nombre completo sigue
// en el DOM (el `span` del texto, recortado sólo por CSS) y la unidad y las áreas también, en un `sr-only`
// dentro de la fila (la etiqueta sólo existe mientras hay hover, foco o toque, y un rastreador no pasa el
// mouse), así que buscadores y lectores de pantalla lo leen todo. El icono es decorativo (`aria-hidden`); aun así
// el `sr-only` de cada fila repite el tipo ("Línea institucional UDA." / "Línea externa."), porque la fila también
// se dibuja suelta (barra del modo editar, dentro de `SidebarEditRow`) y no depende de que se lea el título.
export function LineaRow({
	name,
	institucional = true,
	unidades = [],
	unidadesDeLinea,
	areas = SIN_AREAS,
}: LineaRowProps) {
	const hoverTip = useHoverTip(
		tarjetaDeLinea({ nombre: name, institucional, unidadesDeLinea: unidadesDeLinea ?? unidades, areas }),
		'right',
		{ touch: true },
	);
	const textoOculto = textoOcultoDeLinea({ institucional, unidades, areas });

	return (
		<div className={styles.lineRow} tabIndex={0} {...hoverTip.handlers}>
			<span className={styles.lineRowIconBox}>
				{institucional ? <EscudoUda className={styles.lineRowUdaMark} /> : <FlaskConical size={16} className={styles.lineRowIcon} aria-hidden="true" />}
			</span>
			<span className={styles.lineRowText}>{name}</span>
			<span className="sr-only">{textoOculto}</span>
			{hoverTip.tip}
		</div>
	);
}
